import Shape from './Shape';
import Point from '@/geom/Point';
import BezierPathCommand from '@/geom/BezierPathCommand';
import Location from '@/gis/Location';
import LangUtils from '@/utils/LangUtils';
import SVGUtils from '@/utils/SVGUtils';
import GeomUtils from '@/utils/GeomUtils';
import BezierPathUtils from '@/utils/BezierPathUtils';
import ObjectUtils from '@/utils/ObjectUtils';
import TokenResolver from '@/token/TokenResolver';

export default class MapObjectShape extends Shape {
  constructor(mapObject, style, selectedStyle, projection, language) {
    super(mapObject.uid, mapObject.hidden);
    this.mapObject = mapObject;
    this.normalStyle = style;
    this.selectedStyle = selectedStyle;
    this.statusVisibleStyle = null;
    this.projection = projection;
    this.language = language;
    this._geoPoints = null;
    this.points = null;

    this._extractGeoPoints();
  }

  getName() {
    if (this.mapObject.name) {
      return LangUtils.valueForLanguage(this.mapObject.name, this.language);
    } else {
      return '';
    }
  }

  getSummary() {
    if (this.mapObject.summary) {
      var summary = LangUtils.valueForLanguage(this.mapObject.summary, this.language);
      if (summary) {
        summary = TokenResolver.resolveTextTokens(this.mapObject, summary);
      }
      return summary;
    } else {
      return null;
    }
  }

  getDescription() {
    if (this.mapObject._htmlDescription) {
      return LangUtils.valueForLanguage(this.mapObject._htmlDescription, this.language);
    } else if (this.mapObject.description) {
      return LangUtils.valueForLanguage(this.mapObject.description, this.language);
    } else {
      return null;
    }
  }

  getObservation() {
    return LangUtils.valueForLanguage(this.mapObject.observation, this.language);
  }

  getPlaceInformation() {
    return this.mapObject.placeInformation;
  }

  getTrailInformation() {
    return this.mapObject.trailInformation;
  }

  getCustomInformation() {
    return this.mapObject.customInformation;
  }

  getTabs() {
    return this.mapObject.tabs;
  }

  getType() {
    return this.mapObject.type;
  }

  getIconFileName() {
    return this.mapObject.iconFileName;
  }

  getIconUrl() {
    return this.mapObject.iconUrl;
  }

  isHideable() {
    return this.mapObject.hideable;
  }

  getSections() {
    return this.mapObject.sections;
  }

  get geoPoints() {
    return this.getGeoPoints(true);
  }

  getGeoPoints(allowFallbackOnPoints = true) {
    if (this._geoPoints == null && allowFallbackOnPoints && this.points != null) {
      const locations = [];

      for (var pointsList of this.points) {
        const locationsList = [];
        for (var point of pointsList) {
          const location = this.projection.pointToLocation(point);
          locationsList.push(location);
        }
        locations.push(locationsList);
      }

      return locations;
    } else {
      return this.geoPoints;
    }
  }

  getTags() {
    return this.mapObject.tags;
  }

  _getCoordinates(projectionProxy, featureType) {
    const coordinates = [];
    if (this._geoPoints) {
      if (this.geoPoints.length == 1) {
        var coordinateContainer = coordinates;
        if (featureType == 'Polygon') {
          coordinateContainer = [];
          coordinates.push(coordinateContainer);
        }

        for (var geoPoint of this._geoPoints[0]) {
          const worldLocation = projectionProxy.toWorldLocation(geoPoint);
          coordinateContainer.push([worldLocation.longitude, worldLocation.latitude]);
        }
      } else {
        for (var geoPoints of this._geoPoints) {
          var subGeometry = [];

          if (featureType == 'MultiPolygon') {
            coordinates.push([subGeometry]);
          } else {
            coordinates.push(subGeometry);
          }

          for (geoPoint of geoPoints) {
            const worldLocation = projectionProxy.toWorldLocation(geoPoint);
            subGeometry.push([worldLocation.longitude, worldLocation.latitude]);
          }
        }
      }
    } else if (this.points) {
      if (this.points.length == 1) {
        coordinateContainer = coordinates;
        if (featureType == 'Polygon') {
          coordinateContainer = [];
          coordinates.push(coordinateContainer);
        }

        for (var point of this.points[0]) {
          const worldLocation = projectionProxy.toWorldLocation(point);
          coordinateContainer.push([worldLocation.longitude, worldLocation.latitude]);
        }
      } else {
        for (var subPoints of this.points) {
          subGeometry = [];
          if (featureType == 'MultiPolygon') {
            coordinates.push([subGeometry]);
          } else {
            coordinates.push(subGeometry);
          }

          for (point of subPoints) {
            const worldLocation = projectionProxy.toWorldLocation(point);
            subGeometry.push([worldLocation.longitude, worldLocation.latitude]);
          }
        }
      }
    }
    return coordinates
  }

  _getProperties(baseOSMZoom, projectionProxy) {
    const properties = {
      id: this.getId(),
      type: 'shape',
      interactive: this.isInteractive(),
      hidden: this.isHidden(),
    };

    if (this.normalStyle) {
      properties.hasScalableStrokeWidth = this.normalStyle.scalableStrokeWidth
        ? this.normalStyle.scalableStrokeWidth
        : false;
      properties.strokeColor = this.normalStyle.strokeColor;
      properties.fillColor = this.normalStyle.fillColor;
      properties.hasFillColor = this.normalStyle.fillColor != null;
      if (this.normalStyle.strokePattern) {
        properties.strokePattern = this.normalStyle.strokePattern;
        properties.hasStrokePattern = true;
      } else {
        properties.hasStrokePattern = false;
      }
      this._addStrokeWidthProperties(
        properties,
        'strokeWidth',
        this.normalStyle.strokeWidth,
        this.normalStyle.scalableStrokeWidth,
        baseOSMZoom,
        projectionProxy
      );
    }

    if (this.selectedStyle) {
      properties.selectedStrokeColor = this.selectedStyle.strokeColor;
      properties.selectedFillColor = this.selectedStyle.fillColor;
      this._addStrokeWidthProperties(
        properties,
        'selectedStrokeWidth',
        this.selectedStyle.strokeWidth,
        this.selectedStyle.scalableStrokeWidth == true &&
        this.normalStyle.scalableStrokeWidth == true,
        baseOSMZoom,
        projectionProxy
      );
    } else {
      properties.selectedStrokeColor = '#fffff';
      this._addStrokeWidthProperties(
        properties,
        'selectedStrokeWidth',
        0,
        false,
        baseOSMZoom,
        projectionProxy
      );
    }

    if (this.statusVisibleStyle) {
      properties.statusVisibleStrokeColor = this.statusVisibleStyle.strokeColor;
      properties.statusVisibleFillColor = this.statusVisibleStyle.fillColor;
      this._addStrokeWidthProperties(
        properties,
        'statusVisibleStrokeWidth',
        this.statusVisibleStyle.strokeWidth,
        this.statusVisibleStyle.scalableStrokeWidth,
        baseOSMZoom,
        projectionProxy
      );
    } else if (this.normalStyle) {
      properties.statusVisibleStrokeColor = this.normalStyle.strokeColor;
      properties.statusVisibleFillColor = this.normalStyle.fillColor;
      this._addStrokeWidthProperties(
        properties,
        'statusVisibleStrokeWidth',
        this.normalStyle.strokeWidth,
        this.normalStyle.scalableStrokeWidth,
        baseOSMZoom,
        projectionProxy
      );
    }
    return properties
  }

  toGeoJSONFeature(baseOSMZoom, projectionProxy) {
    const properties = this._getProperties(baseOSMZoom, projectionProxy)

    var featureType = 'LineString';
    switch (this.shapeType) {
      case Shape.Type.LINE_STRING:
        featureType = 'LineString';
        break;
      case Shape.Type.MULTI_LINE_STRING:
        featureType = 'MultiLineString';
        break;
      case Shape.Type.POLYGON:
        featureType = 'Polygon';
        break;
      case Shape.Type.MULTI_POLYGON:
        featureType = 'MultiPolygon';
        break;
      default:
        featureType = 'LineString';
        break;
    }

    const coordinates = this._getCoordinates(projectionProxy, featureType)

    const feature = {
      type: 'feature',
      properties: properties,
      geometry: {
        type: featureType,
        coordinates: coordinates,
      },
    };

    return feature;
  }

  _extractGeoPoints() {
    if (this._geoPoints == null && this.points == null) {
      const extractOnlyPoints =
        this.mapObject.location == null &&
        (this.mapObject.geometryLocations == null || this.mapObject.geometryLocations.length > 0) &&
        this.mapObject.origin != null;
      var origin = this.mapObject.origin;
      if (origin == null && this.mapObject.location != null) {
        origin = this.projection.locationToPoint(this.mapObject.location);
      }

      this._geoPoints = extractOnlyPoints ? null : [];
      this.points = extractOnlyPoints ? [] : null;

      if (origin != null) {
        var commands = null;

        if (this.mapObject.encodedGeometry != null) {
          commands = BezierPathUtils.createPathFromEncodedPolyline(
            this.mapObject.getEncodedGeometry()
          );
        } else if (this.mapObject.svgPath != null) {
          commands = SVGUtils.parseSVGString(this.mapObject.svgPath);
        } else if (this.mapObject.geometryLocations != null) {
          var locations = [];
          for (var location of this.mapObject.geometryLocations) {
            locations.push(location);
          }
          this.geoPoints.push(locations);
        } else if (this.mapObject.pathCommands != null) {
          commands = this.mapObject.pathCommands;
        }

        if (commands != null) {
          locations = null;
          var linePoints = null;

          for (var command of commands) {
            if (
              command.type == BezierPathCommand.Type.MOVE_TO_POINT ||
              (locations == null && linePoints == null)
            ) {
              if (!extractOnlyPoints) {
                locations = [];
                this._geoPoints.push(locations);
              } else {
                linePoints = [];
                this.points.push(linePoints);
              }
            }

            if (
              command.type == BezierPathCommand.Type.MOVE_TO_POINT ||
              command.type == BezierPathCommand.Type.ADD_LINE_TO_POINT
            ) {
              var x = origin.x + command.point.x;
              var y = origin.y + command.point.y;

              if (extractOnlyPoints) {
                linePoints.push(new Point(x, y));
              } else {
                location = this.projection.pointToLocation(new Point(x, y));
                location.latitude = Math.round(location.latitude * 100000.0) / 100000.0;
                location.longitude = Math.round(location.longitude * 100000.0) / 100000.0;
                locations.push(location);
              }
            } else if (command.type == BezierPathCommand.Type.ADD_CURVE_TO_POINT) {
              var point0 = null;
              if (extractOnlyPoints) {
                point0 = linePoints.length > 0 ? linePoints[linePoints.length - 1] : origin;
              } else {
                point0 =
                  locations.length > 0
                    ? this.projection.locationToPoint(locations[locations.length - 1])
                    : origin;
              }
              var point1 = GeomUtils.addPoints(origin, command.controlPoint1);
              var point2 = GeomUtils.addPoints(origin, command.controlPoint2);
              var point3 = GeomUtils.addPoints(origin, command.point);

              var increment = 0.5;
              for (var i = increment; i <= 1.0; i = i + increment) {
                var linePoint = BezierPathUtils.getPointOnCubicBezier(
                  point0,
                  point1,
                  point2,
                  point3,
                  i
                );

                if (extractOnlyPoints) {
                  linePoints.push(linePoint);
                } else {
                  var lineLocation = this.projection.pointToLocation(linePoint);
                  lineLocation.latitude = Math.round(lineLocation.latitude * 100000.0) / 100000.0;
                  lineLocation.longitude = Math.round(lineLocation.longitude * 100000.0) / 100000.0;
                  locations.push(lineLocation);
                }
              }
            } else if (command.type == BezierPathCommand.Type.ADD_QUAD_CURVE_TO_POINT) {
              point0 = null;
              if (extractOnlyPoints) {
                point0 = linePoints.length > 0 ? linePoints[linePoints.length - 1] : origin;
              } else {
                point0 =
                  locations.length > 0
                    ? this.projection.locationToPoint(locations[locations.length - 1])
                    : origin;
              }
              point1 = GeomUtils.addPoints(origin, command.controlPoint1);
              point2 = GeomUtils.addPoints(origin, command.point);

              increment = 0.5;
              for (i = increment; i <= 1.0; i = i + increment) {
                linePoint = BezierPathUtils.getPointOnQuadraticBezier(point0, point1, point2, i);

                if (extractOnlyPoints) {
                  linePoints.push(linePoint);
                } else {
                  lineLocation = this.projection.pointToLocation(linePoint);
                  lineLocation.latitude = Math.round(lineLocation.latitude * 100000.0) / 100000.0;
                  lineLocation.longitude = Math.round(lineLocation.longitude * 100000.0) / 100000.0;
                  locations.push(lineLocation);
                }
              }
            } else if (command.type == BezierPathCommand.Type.CLOSE_PATH) {
              if (extractOnlyPoints) {
                if (linePoints != null && linePoints.length > 0) {
                  linePoints.push(linePoints[0]);
                }
              } else {
                if (locations != null && locations.length > 0) {
                  locations.push(locations[0]);
                }
              }
            }
          }
        }

        var topLatitude = -90.0;
        var leftLongitude = 180.0;
        var bottomLatitude = 90.0;
        var rightLongitude = -180.0;

        if (this._geoPoints != null) {
          for (locations of this._geoPoints) {
            for (var geopoint of locations) {
              topLatitude = Math.max(topLatitude, geopoint.latitude);
              leftLongitude = Math.min(leftLongitude, geopoint.longitude);
              bottomLatitude = Math.min(bottomLatitude, geopoint.latitude);
              rightLongitude = Math.max(rightLongitude, geopoint.longitude);
            }
          }

          this.topLeftBound = new Location(topLatitude, leftLongitude);
          this.bottomRightBound = new Location(bottomLatitude, rightLongitude);
        } else {
          var top = -999999.0;
          var left = 999999.0;
          var bottom = 999999.0;
          var right = -999999.0;

          for (linePoints of this.points) {
            for (var point of linePoints) {
              top = Math.max(top, point.y);
              left = Math.min(left, point.x);
              bottom = Math.min(bottom, point.y);
              right = Math.max(right, point.x);
            }
          }

          this.topLeftBound = this.projection.pointToLocation(new Point(left, top));
          this.bottomRightBound = this.projection.pointToLocation(new Point(right, bottom));
        }
      }
    }

    var hasFillColor = this.normalStyle && this.normalStyle.fillColor ? true : false;

    if (this._geoPoints != null) {
      if (this._geoPoints.length == 1) {
        locations = this._geoPoints[0];
        this.shapeType =
          locations.length >= 3 &&
            ObjectUtils.isEqual(locations[0], locations[locations.length - 1]) &&
            hasFillColor
            ? Shape.Type.POLYGON
            : Shape.Type.LINE_STRING;
      } else if (this._geoPoints.length > 1) {
        locations = this._geoPoints[0];
        this.shapeType =
          locations.length >= 3 &&
            ObjectUtils.isEqual(locations[0], locations[locations.length - 1]) &&
            hasFillColor
            ? Shape.Type.MULTI_POLYGON
            : Shape.Type.MULTI_LINE_STRING;
      }
    } else if (this.points != null) {
      if (this.points.length == 1) {
        var pointList = this.points[0];
        this.shapeType =
          pointList.length >= 3 &&
            ObjectUtils.isEqual(pointList[0], pointList[pointList.length - 1]) &&
            hasFillColor
            ? Shape.Type.POLYGON
            : Shape.Type.LINE_STRING;
      } else if (this.points.length > 1) {
        pointList = this.points[0];
        this.shapeType =
          pointList.length >= 3 &&
            ObjectUtils.isEqual(pointList[0], pointList[pointList.length - 1]) &&
            hasFillColor
            ? Shape.Type.MULTI_POLYGON
            : Shape.Type.MULTI_LINE_STRING;
      }
    }
  }

  _addStrokeWidthProperties(
    feature,
    propertyName,
    strokeWidth,
    scalable,
    baseOSMZoom,
    projectionProxy
  ) {
    if (strokeWidth == undefined) {
      strokeWidth = 0;
    }

    if (scalable == true) {
      var step = 2;
      for (var zoom = baseOSMZoom - 1; zoom >= 0; zoom--) {
        const worldZoomStop = projectionProxy.toWorldZoom(zoom);
        feature[this._zoomStopProperty(worldZoomStop, propertyName)] = strokeWidth / step;
        step = step + 1;
      }

      step = 1;
      for (zoom = baseOSMZoom; zoom <= 30; zoom++) {
        const worldZoomStop = projectionProxy.toWorldZoom(zoom);
        feature[this._zoomStopProperty(worldZoomStop, propertyName)] = strokeWidth * step;
        step = step + 1;
      }
    } else {
      const worldZoomStop = projectionProxy.toWorldZoom(0.0);
      feature[this._zoomStopProperty(worldZoomStop, propertyName)] = strokeWidth;
    }
  }
}
